'use client';

import { Loader2, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useMomento } from '@/hooks/useMomento';
import { toast } from 'sonner';

/**
 * Tool UI for Instant Preview tool
 * Shows preview generation status and polls for completion
 */
export function InstantPreviewToolUI({ part, status }: any) {
  const { selectedMoment, setSelectedMoment } = useMomento();
  const result = part.result;
  const [previewStatus, setPreviewStatus] = useState<'polling' | 'ready' | 'failed'>('polling');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Poll for preview completion
  useEffect(() => {
    if (status === 'done' && result?.success && result?.previewSessionId) {
      const sessionId = result.previewSessionId;
      let pollCount = 0;
      const maxPolls = 60; // 2 minutes max (2s intervals)

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const response = await fetch(`/api/momento/preview-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ previewSessionId: sessionId }),
          });
          const data = await response.json();

          if (data.status === 'ready' && data.previewUrl) {
            setPreviewStatus('ready');
            setPreviewUrl(data.previewUrl);
            clearInterval(pollInterval);

            // Update selected moment with preview URL
            if (selectedMoment) {
              setSelectedMoment({
                ...selectedMoment,
                previewUrl: data.previewUrl,
                isPreview: true,
                expiresAt: data.expiresAt,
                previewSessionId: sessionId,
              });
            }

            toast.success('Preview ready! Watch it on the right →');
          } else if (data.status === 'failed') {
            setPreviewStatus('failed');
            setError(data.error || 'Preview generation failed');
            clearInterval(pollInterval);
            toast.error('Preview generation failed');
          } else if (pollCount >= maxPolls) {
            setPreviewStatus('failed');
            setError('Preview generation timeout');
            clearInterval(pollInterval);
            toast.error('Preview generation timeout');
          }
        } catch (err) {
          console.error('[InstantPreviewToolUI] Poll error:', err);
          if (pollCount >= maxPolls) {
            setPreviewStatus('failed');
            setError('Failed to check preview status');
            clearInterval(pollInterval);
          }
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }
  }, [status, result, selectedMoment, setSelectedMoment]);

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Starting preview generation...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Preview generation failed
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (previewStatus === 'polling') {
    const estimatedTime = result.estimatedTime || 30;
    return (
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 overflow-hidden">
        <div className="flex items-center gap-2 p-3">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Generating preview...
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Estimated time: ~{estimatedTime}s
            </p>
          </div>
        </div>
        <div className="w-full bg-blue-100 dark:bg-blue-900/40 h-1">
          <div className="h-full bg-blue-600 animate-pulse w-2/3"></div>
        </div>
      </div>
    );
  }

  if (previewStatus === 'ready') {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <div className="flex-1">
          <p className="text-sm font-medium text-green-900 dark:text-green-100">
            Preview ready!
          </p>
          <p className="text-xs text-green-700 dark:text-green-300 mt-1">
            Watch it in the video panel →
          </p>
        </div>
        <Eye className="w-4 h-4 text-green-600" />
      </div>
    );
  }

  if (previewStatus === 'failed') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Preview failed
          </p>
          {error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
}
